import { useState, useEffect, useRef, useMemo } from 'react';
import './PassengerAllocations.css';
import { useTranslation } from 'react-i18next';
import type { ApiError } from '../../services/api';
import { fetchTransportRoutes } from '../../services/transportRouteApi';
import type { TransportRouteRecord } from '../../services/transportRouteApi';
import { fetchTransportAllocations } from '../../services/transportAllocationApi';
import type { TransportAllocationRecord } from '../../services/transportAllocationApi';
import { printPassengerList } from '../../utils/printPassengerList';
import { useCurrentSchoolName } from '../../hooks/useCurrentSchoolName';

function getStudentName(rec: TransportAllocationRecord): string {
    if (rec.student?.name) return rec.student.name;
    if (rec.student) return [rec.student.first_name, rec.student.last_name].filter(Boolean).join(' ');
    return '';
}

function getRouteName(rec: TransportAllocationRecord): string {
    return rec.route_stop?.route?.title ?? rec.transport_route?.title ?? '';
}

function getFatherName(rec: TransportAllocationRecord): string {
    return rec.student?.father_name ?? '';
}

function getMotherName(rec: TransportAllocationRecord): string {
    return rec.student?.mother_name ?? '';
}

function getAddress(rec: TransportAllocationRecord): string {
    return rec.student?.address ?? '';
}

function getContactNumber(rec: TransportAllocationRecord): string {
    return rec.student?.father_contact_number ?? '';
}

function getClassName(rec: TransportAllocationRecord): string {
    return rec.student?.current_class?.name ?? '';
}

export default function PassengerAllocations() {
    const { t: _t } = useTranslation();
    const schoolName = useCurrentSchoolName();

    /* ── Data from API ── */
    const [allocations, setAllocations] = useState<TransportAllocationRecord[]>([]);
    const [routes, setRoutes] = useState<TransportRouteRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [allocLoading, setAllocLoading] = useState(false);

    /* ── Draft vs. applied route filter — mirrors the Student List's search/filter pattern ── */
    const [routeInput, setRouteInput] = useState('');
    const [appliedRouteFilter, setAppliedRouteFilter] = useState('');
    const [apiError, setApiError] = useState('');

    /* ── Load initial data ── */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [allocData, routeData] = await Promise.all([
                    fetchTransportAllocations({ perPage: 100 }),
                    fetchTransportRoutes(),
                ]);
                if (cancelled) return;
                setAllocations(allocData);
                setRoutes(routeData);
            } catch (err) {
                if (!cancelled) setApiError((err as ApiError).message || 'Failed to load data.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    /* ── Reload allocations when the applied route filter changes (skips the initial mount, which the effect above already covers) ── */
    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) { isFirstRun.current = false; return; }
        let cancelled = false;
        (async () => {
            setAllocLoading(true);
            try {
                const allocData = await fetchTransportAllocations({ transportRouteId: appliedRouteFilter || undefined, perPage: 100 });
                if (!cancelled) setAllocations(allocData);
            } catch (err) {
                if (!cancelled) setApiError((err as ApiError).message || 'Failed to load allocations.');
            } finally {
                if (!cancelled) setAllocLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [appliedRouteFilter]);

    /* ── Derive capacity from routes + allocations ── */
    const capacityData = useMemo(() => {
        const activeCountByRoute = new Map<string, number>();
        for (const a of allocations) {
            if (!a.is_active) continue;
            const routeId = a.route_stop?.route?.id;
            if (routeId) activeCountByRoute.set(routeId, (activeCountByRoute.get(routeId) ?? 0) + 1);
        }
        return routes
            .filter((r) => r.vehicle)
            .map((r) => ({
                route_id: r.id,
                route_name: r.title,
                vehicle_number: r.vehicle!.vehicle_number,
                seating_capacity: r.vehicle!.seating_capacity,
                active_allocations: activeCountByRoute.get(r.id) ?? 0,
            }));
    }, [routes, allocations]);

    const handleSearch = () => setAppliedRouteFilter(routeInput);
    const handleClear = () => { setRouteInput(''); setAppliedRouteFilter(''); };
    const hasActiveFilter = appliedRouteFilter !== '';

    /* Printing needs a single route in context — the sheet's header names it, and
       printing "all routes" isn't a meaningful manifest. */
    const canPrint = appliedRouteFilter !== '';
    const handlePrint = () => {
        const routeName = routes.find((r) => r.id === appliedRouteFilter)?.title ?? '';
        printPassengerList({
            schoolName: schoolName ?? undefined,
            routeName,
            rows: allocations.map((a) => ({
                studentName: getStudentName(a),
                fatherName: getFatherName(a),
                motherName: getMotherName(a),
                className: getClassName(a),
                address: getAddress(a),
                contact: getContactNumber(a),
            })),
        });
    };

    if (loading) {
        return (
            <div className="alloc">
                <div className="alloc__header">
                    <div className="alloc__header-text">
                        <h1 className="alloc__title">Passenger Allocations</h1>
                        <p className="alloc__subtitle">Loading…</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="alloc">
            {apiError && (
                <div className="alloc__toast" style={{ background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' }}>
                    <span>{apiError}</span>
                    <button className="alloc__toast-dismiss" onClick={() => setApiError('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            <div className="alloc__header">
                <div className="alloc__header-text">
                    <h1 className="alloc__title">Passenger Allocations</h1>
                    <p className="alloc__subtitle">View students assigned to transport routes and monitor seat capacity.</p>
                </div>
                <div className="alloc__header-actions">
                    <select className="alloc__filter" value={routeInput} onChange={(e) => setRouteInput(e.target.value)}>
                        <option value="">All Routes</option>
                        {routes.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
                    </select>
                    <button className="alloc__search-btn" onClick={handleSearch}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        Search
                    </button>
                    {hasActiveFilter && (
                        <button className="alloc__clear-btn" onClick={handleClear}>Clear</button>
                    )}
                    <button
                        className="alloc__print-btn"
                        onClick={handlePrint}
                        disabled={!canPrint}
                        title={canPrint ? 'Print this route\'s passenger list' : 'Select a route to print its passenger list'}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9" />
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            <rect x="6" y="14" width="12" height="8" />
                        </svg>
                        Print
                    </button>
                </div>
            </div>

            {/* ── Capacity Cards ── */}
            <div className="alloc__capacity-row">
                {capacityData.map((c) => {
                    const pct = Math.round((c.active_allocations / c.seating_capacity) * 100);
                    const isFull = c.active_allocations >= c.seating_capacity;
                    return (
                        <div className="alloc__cap-card" key={c.route_id}>
                            <div className="alloc__cap-header">
                                <span className="alloc__cap-route">{c.route_name}</span>
                                <span className="alloc__cap-vehicle">{c.vehicle_number}</span>
                            </div>
                            <div className="alloc__cap-stats">
                                <span className={`alloc__cap-count ${isFull ? 'alloc__cap-count--full' : ''}`}>{c.active_allocations} / {c.seating_capacity}</span>
                                <span className="alloc__cap-pct">{pct}%</span>
                            </div>
                            <div className="alloc__cap-bar">
                                <div className={`alloc__cap-fill ${isFull ? 'alloc__cap-fill--full' : ''}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ── Allocations Grid ── */}
            {hasActiveFilter && !allocLoading && (
                <div className="alloc__total-row">
                    <span className="alloc__total-count">{allocations.length}</span> passenger{allocations.length === 1 ? '' : 's'} on this route
                </div>
            )}
            <div className="alloc__table-wrap">
                <table className="alloc__table">
                    <thead>
                        <tr>
                            <th className="alloc__th">Student Name</th>
                            <th className="alloc__th">Father</th>
                            <th className="alloc__th">Mother</th>
                            <th className="alloc__th">Route</th>
                            <th className="alloc__th">Class</th>
                            <th className="alloc__th">Address</th>
                            <th className="alloc__th">Contact</th>
                        </tr>
                    </thead>
                    <tbody>
                        {allocLoading && (
                            <tr><td className="alloc__td alloc__td--loading" colSpan={7}>Loading…</td></tr>
                        )}
                        {!allocLoading && allocations.map((a) => {
                            const className = getClassName(a);
                            const fatherName = getFatherName(a);
                            const motherName = getMotherName(a);
                            const address = getAddress(a);
                            const contact = getContactNumber(a);
                            return (
                                <tr className="alloc__row" key={a.id}>
                                    <td className="alloc__td alloc__td--student">{getStudentName(a)}</td>
                                    <td className="alloc__td">{fatherName || <span className="alloc__muted">—</span>}</td>
                                    <td className="alloc__td">{motherName || <span className="alloc__muted">—</span>}</td>
                                    <td className="alloc__td">{getRouteName(a)}</td>
                                    <td className="alloc__td">{className || <span className="alloc__muted">—</span>}</td>
                                    <td className="alloc__td">{address || <span className="alloc__muted">—</span>}</td>
                                    <td className="alloc__td">
                                        {contact ? <a href={`tel:${contact}`} className="alloc__contact-link">{contact}</a> : <span className="alloc__muted">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
